import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "../api";
import PaymentScheduleFields from "./PaymentScheduleFields";

const AddAccountModal = ({ accountTypes, profileId, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: "",
    account_type: "",
    account_number: "",
    company_provider: "",
    url: "",
    status: "Active",
    notes: "",
    payment_due_date: "",
    payment_recurrence: "Monthly",
    amount_due: "",
    billing_mode: "statements",
    auto_pay: false,
    same_day_mode: true,
    day_of_month: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file (JPG, PNG, GIF)");
        return;
      }
      if (file.size > 1024 * 1024) {
        toast.error("Logo file must be smaller than 1MB");
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { same_day_mode: _sdm, day_of_month: _dom, ...persisted } = formData;
      const payload = {
        ...persisted,
        profile_id: profileId,
        amount_due: persisted.amount_due === "" ? null : parseFloat(persisted.amount_due),
      };
      const response = await axios.post(`${API}/accounts`, payload);
      const accountId = response.data.id;

      if (logoFile) {
        const logoFormData = new FormData();
        logoFormData.append("logo", logoFile);
        await axios.post(`${API}/accounts/${accountId}/logo`, logoFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await onAdd();
      onClose();
      toast.success("Account created successfully!");
    } catch (error) {
      console.error("Failed to create account:", error);
      if (error.response?.status === 401) {
        toast.error("Your session has expired. Please sign in again.");
      } else {
        const detail = formatApiErrorDetail(error.response?.data?.detail);
        toast.error(detail || "Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="add-account-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Account</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Account Logo (Optional)</label>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="w-32 h-16 rounded-lg object-contain border bg-white" />
                ) : (
                  <div className="w-32 h-16 rounded-lg bg-gray-100 flex items-center justify-center border">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" id="logo-upload" data-testid="add-account-logo-input" />
                <label htmlFor="logo-upload" className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  {logoFile ? "Change Logo" : "Upload Logo"}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF up to 1MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Account Name</label>
            <input
              type="text"
              required
              data-testid="add-account-name-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Account Type</label>
            <select
              required
              data-testid="add-account-type-select"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.account_type}
              onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
            >
              <option value="">Select type...</option>
              {accountTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Account Number (Optional)</label>
            <input
              type="text"
              data-testid="add-account-number-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Company/Provider (Optional)</label>
            <input
              type="text"
              data-testid="add-account-provider-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.company_provider}
              onChange={(e) => setFormData({ ...formData, company_provider: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">URL (Optional)</label>
            <input
              type="url"
              placeholder="https://..."
              data-testid="add-account-url-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
            <textarea
              data-testid="add-account-notes-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          <PaymentScheduleFields
            dueDate={formData.payment_due_date}
            recurrence={formData.payment_recurrence}
            sameDayMode={formData.same_day_mode}
            dayOfMonth={formData.day_of_month}
            onChange={(patch) => setFormData((prev) => ({
              ...prev,
              ...(patch.dueDate !== undefined ? { payment_due_date: patch.dueDate } : {}),
              ...(patch.recurrence !== undefined ? { payment_recurrence: patch.recurrence } : {}),
              ...(patch.sameDayMode !== undefined ? { same_day_mode: patch.sameDayMode } : {}),
              ...(patch.dayOfMonth !== undefined ? { day_of_month: patch.dayOfMonth } : {}),
            }))}
            testIdPrefix="add-account"
          />

          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.auto_pay}
                onChange={(e) => setFormData({ ...formData, auto_pay: e.target.checked })}
                data-testid="add-account-auto-pay"
                className="mt-1 h-4 w-4 accent-blue-600"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-800">Automatic withdrawal</span>
                <span className="block text-xs text-gray-500">
                  This account is paid automatically on the due date. It will still show up in Due Soon
                  as a reminder, then be marked paid automatically once the due date passes.
                </span>
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Billing mode</label>
            <div className="mt-1 flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="billing_mode"
                  value="statements"
                  checked={formData.billing_mode === "statements"}
                  onChange={(e) => setFormData({ ...formData, billing_mode: e.target.value })}
                  data-testid="add-account-billing-mode-statements"
                />
                <span className="text-sm">Statements (12-month grid)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="billing_mode"
                  value="invoices"
                  checked={formData.billing_mode === "invoices"}
                  onChange={(e) => setFormData({ ...formData, billing_mode: e.target.value })}
                  data-testid="add-account-billing-mode-invoices"
                />
                <span className="text-sm">Invoices (irregular)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Amount Due (Optional)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              data-testid="add-account-amount-due-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={formData.amount_due}
              onChange={(e) => setFormData({ ...formData, amount_due: e.target.value })}
              disabled={formData.billing_mode === "invoices"}
            />
            {formData.billing_mode === "invoices" && (
              <p className="text-xs text-gray-500 mt-1">Auto-summed from unpaid invoices.</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              data-testid="add-account-cancel-button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="add-account-submit-button"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAccountModal;
